import React, { useState } from 'react';
import { Building2, Home, Wrench, CreditCard, CheckCircle2, Clock, Users, Plus } from 'lucide-react';
import { useAppContext, MaintenanceRequest } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
export const FacilityManagement: React.FC = () => {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const [viewRole, setViewRole] = useState<'Tenant' | 'Landlord'>('Tenant');
  // Modals
  const [isMaintenanceOpen, setIsMaintenanceOpen] = useState(false);
  const [isPayRentOpen, setIsPayRentOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  // Form states
  const [issueType, setIssueType] = useState('Plumbing');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('Medium');
  // Filter data based on role
  const myProperties = state.properties.filter(p => p.role === viewRole);
  const myRequests = state.maintenanceRequests.filter(r => myProperties.some(p => p.id === r.propertyId));
  const handlePayRent = () => {
    const property = myProperties.find(p => p.id === selectedPropertyId);
    if (!property) return;
    if (property.monthlyRent > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance to pay rent');
      return;
    }
    processTransaction(-property.monthlyRent, 'Facility', `Rent Payment - ${property.address}`);
    toast.success('Rent Paid Successfully!', {
      description: `${formatCurrency(property.monthlyRent)} paid for ${property.address}`,
      icon: <CheckCircle2 className="text-brand-primary" data-id="element-604" />
    });
    setIsPayRentOpen(false);
  };
  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPropertyId || !description) return;
    const newRequest: MaintenanceRequest = {
      id: `req_${Date.now()}`,
      propertyId: selectedPropertyId,
      issueType,
      description,
      urgency,
      status: 'Pending',
      createdAt: new Date().toISOString()
    };
    dispatch({
      type: 'ADD_MAINTENANCE_REQUEST',
      payload: newRequest
    });
    toast.success('Maintenance request submitted successfully');
    setIsMaintenanceOpen(false);
    setDescription('');
    setIssueType('Plumbing');
  };
  const updateRequestStatus = (id: string, status: string) => {
    dispatch({
      type: 'UPDATE_MAINTENANCE_STATUS',
      payload: {
        id,
        status
      }
    });
    toast.success(`Request marked as ${status}`);
  };
  return <div className="space-y-6" data-id="element-605">
      {/* Header & Role Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-606">
        <div data-id="element-607">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-608">
            Facility Management
          </h1>
          <p className="text-brand-text-light text-sm" data-id="element-609">
            Manage properties, pay rent, and track maintenance.
          </p>
        </div>

        <div className="bg-gray-100 p-1 rounded-xl inline-flex shrink-0" data-id="element-610">
          <button onClick={() => setViewRole('Tenant')} className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${viewRole === 'Tenant' ? 'bg-white text-brand-text-dark shadow-sm' : 'text-gray-500 hover:text-brand-text-dark'}`} data-id="element-611">
            Tenant View
          </button>
          <button onClick={() => setViewRole('Landlord')} className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${viewRole === 'Landlord' ? 'bg-white text-brand-text-dark shadow-sm' : 'text-gray-500 hover:text-brand-text-dark'}`} data-id="element-612">
            Landlord View
          </button>
        </div>
      </div>

      {viewRole === 'Tenant' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-613">
          {/* Properties List */}
          <div className="lg:col-span-2 space-y-6" data-id="element-614">
            <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-615">
              My Properties
            </h3>

            {myProperties.length > 0 ? myProperties.map(property => <div key={property.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 flex flex-col sm:flex-row gap-6" data-id="element-616">
                  <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center text-brand-primary shrink-0" data-id="element-617">
                    <Home size={32} data-id="element-618" />
                  </div>

                  <div className="flex-1" data-id="element-619">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4" data-id="element-620">
                      <div data-id="element-621">
                        <h4 className="font-semibold text-lg text-brand-text-dark" data-id="element-622">
                          {property.address}
                        </h4>
                        <p className="text-sm text-brand-text-light" data-id="element-623">
                          {property.type} • Landlord: {property.landlordName}
                        </p>
                      </div>
                      <div className="text-left sm:text-right" data-id="element-624">
                        <p className="text-sm text-brand-text-light mb-1" data-id="element-625">
                          Monthly Rent
                        </p>
                        <p className="text-xl font-bold text-brand-text-dark" data-id="element-626">
                          {formatCurrency(property.monthlyRent)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-627">
                      <div className="flex items-center gap-2 text-sm" data-id="element-628">
                        <Clock size={16} className="text-brand-accent" data-id="element-629" />
                        <span className="text-gray-600" data-id="element-630">
                          Next due:{' '}
                          <span className="font-semibold text-brand-text-dark" data-id="element-631">
                            {formatDate(property.nextDueDate)}
                          </span>
                        </span>
                      </div>
                      <div className="flex gap-3 w-full sm:w-auto" data-id="element-632">
                        <button onClick={() => {
                  setSelectedPropertyId(property.id);
                  setIsMaintenanceOpen(true);
                }} className="flex-1 sm:flex-none px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-id="element-633">
                          <Wrench size={16} data-id="element-634" /> Request Repair
                        </button>
                        <button onClick={() => {
                  setSelectedPropertyId(property.id);
                  setIsPayRentOpen(true);
                }} className="flex-1 sm:flex-none px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-635">
                          <CreditCard size={16} data-id="element-636" /> Pay Rent
                        </button>
                      </div>
                    </div>
                  </div>
                </div>) : <EmptyState icon={Home} title="No active leases" description="You are not currently listed as a tenant in any property." data-id="element-637" />}
          </div>

          {/* Maintenance Requests */}
          <div className="lg:col-span-1" data-id="element-638">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 h-full" data-id="element-639">
              <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-640">
                Maintenance Requests
              </h3>

              <div className="space-y-4" data-id="element-641">
                {myRequests.length > 0 ? myRequests.map(req => <div key={req.id} className="p-4 rounded-2xl border border-gray-100 hover:border-brand-primary/30 transition-colors" data-id="element-642">
                      <div className="flex justify-between items-start mb-2" data-id="element-643">
                        <span className="font-semibold text-sm text-brand-text-dark" data-id="element-644">
                          {req.issueType}
                        </span>
                        <StatusBadge status={req.status} data-id="element-645" />
                      </div>
                      <p className="text-sm text-brand-text-light line-clamp-2 mb-3" data-id="element-646">
                        {req.description}
                      </p>
                      <div className="flex justify-between items-center text-xs" data-id="element-647">
                        <span className="text-gray-500" data-id="element-648">
                          {formatDate(req.createdAt)}
                        </span>
                        <span className={`font-medium ${req.urgency === 'High' ? 'text-brand-error' : req.urgency === 'Medium' ? 'text-brand-warning' : 'text-brand-primary'}`} data-id="element-649">
                          {req.urgency} Priority
                        </span>
                      </div>
                    </div>) : <div className="text-center py-8" data-id="element-650">
                    <Wrench className="mx-auto text-gray-300 mb-2" size={32} data-id="element-651" />
                    <p className="text-sm text-gray-500" data-id="element-652">
                      No active maintenance requests.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {viewRole === 'Landlord' && <div className="space-y-6" data-id="element-653">
          {/* Landlord Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-654">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-655">
              <div className="flex items-center gap-4 mb-2" data-id="element-656">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600" data-id="element-657">
                  <Building2 size={20} data-id="element-658" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-659">
                  Total Properties
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-660">
                {myProperties.length}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-661">
              <div className="flex items-center gap-4 mb-2" data-id="element-662">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600" data-id="element-663">
                  <Users size={20} data-id="element-664" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-665">
                  Active Tenants
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-666">
                {myProperties.filter(p => p.tenantName).length}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-667">
              <div className="flex items-center gap-4 mb-2" data-id="element-668">
                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-brand-accent" data-id="element-669">
                  <Wrench size={20} data-id="element-670" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-671">
                  Pending Requests
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-672">
                {myRequests.filter(r => r.status === 'Pending').length}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-673">
            {/* Properties List */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-674">
              <div className="flex justify-between items-center mb-6" data-id="element-675">
                <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-676">
                  Portfolio
                </h3>
                <button className="text-sm font-medium text-brand-primary hover:text-brand-primary-dark flex items-center gap-1" data-id="element-677">
                  <Plus size={16} data-id="element-678" /> Add Property
                </button>
              </div>

              <div className="space-y-4" data-id="element-679">
                {myProperties.length > 0 ? myProperties.map(property => <div key={property.id} className="p-4 rounded-2xl border border-gray-100" data-id="element-680">
                      <h4 className="font-semibold text-brand-text-dark mb-1" data-id="element-681">
                        {property.address}
                      </h4>
                      <div className="flex justify-between items-center text-sm" data-id="element-682">
                        <span className="text-brand-text-light" data-id="element-683">
                          Tenant:{' '}
                          <span className="font-medium text-brand-text-dark" data-id="element-684">
                            {property.tenantName || 'Vacant'}
                          </span>
                        </span>
                        <span className="font-bold text-brand-primary" data-id="element-685">
                          {formatCurrency(property.monthlyRent)}/mo
                        </span>
                      </div>
                    </div>) : <EmptyState icon={Building2} title="No properties" description="Add your first property to start managing." data-id="element-686" />}
              </div>
            </div>

            {/* Maintenance Management */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-687">
              <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-688">
                Tenant Requests
              </h3>

              <div className="space-y-4" data-id="element-689">
                {myRequests.length > 0 ? myRequests.map(req => {
              const prop = myProperties.find(p => p.id === req.propertyId);
              return <div key={req.id} className="p-4 rounded-2xl border border-gray-100" data-id="element-690">
                        <div className="flex justify-between items-start mb-2" data-id="element-691">
                          <div data-id="element-692">
                            <span className="font-semibold text-sm text-brand-text-dark" data-id="element-693">
                              {req.issueType}
                            </span>
                            <p className="text-xs text-brand-text-light" data-id="element-694">
                              {prop?.address}
                            </p>
                          </div>
                          <StatusBadge status={req.status} data-id="element-695" />
                        </div>
                        <p className="text-sm text-gray-600 mb-4" data-id="element-696">
                          {req.description}
                        </p>

                        {req.status === 'Pending' && <div className="flex gap-2" data-id="element-697">
                            <button onClick={() => updateRequestStatus(req.id, 'In Progress')} className="flex-1 py-1.5 bg-brand-primary/10 text-brand-primary-dark rounded-lg text-xs font-semibold hover:bg-brand-primary/20 transition-colors" data-id="element-698">
                              Approve & Assign
                            </button>
                          </div>}
                        {req.status === 'In Progress' && <div className="flex gap-2" data-id="element-699">
                            <button onClick={() => updateRequestStatus(req.id, 'Completed')} className="flex-1 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200 transition-colors" data-id="element-700">
                              Mark Completed
                            </button>
                          </div>}
                      </div>;
            }) : <div className="text-center py-8" data-id="element-701">
                    <CheckCircle2 className="mx-auto text-green-400 mb-2" size={32} data-id="element-702" />
                    <p className="text-sm text-gray-500" data-id="element-703">
                      All caught up! No pending requests.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {/* Pay Rent Modal */}
      <Modal isOpen={isPayRentOpen} onClose={() => setIsPayRentOpen(false)} title="Pay Rent" data-id="element-704">
        {selectedPropertyId && <div className="space-y-6" data-id="element-705">
            <div className="bg-gray-50 rounded-xl p-4 text-center" data-id="element-706">
              <p className="text-sm text-brand-text-light mb-1" data-id="element-707">Amount Due</p>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-708">
                {formatCurrency(myProperties.find(p => p.id === selectedPropertyId)?.monthlyRent || 0)}
              </p>
            </div>

            <div className="flex justify-between items-center p-4 border border-gray-200 rounded-xl" data-id="element-709">
              <div className="flex items-center gap-3" data-id="element-710">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary" data-id="element-711">
                  <CreditCard size={20} data-id="element-712" />
                </div>
                <div data-id="element-713">
                  <p className="font-medium text-sm text-brand-text-dark" data-id="element-714">
                    Pay from Wallet
                  </p>
                  <p className="text-xs text-brand-text-light" data-id="element-715">
                    Balance: {formatCurrency(state.user?.walletBalance || 0)}
                  </p>
                </div>
              </div>
              <div className="w-4 h-4 rounded-full border-4 border-brand-primary bg-white" data-id="element-716"></div>
            </div>

            <button onClick={handlePayRent} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors shadow-md" data-id="element-717">
              Confirm Payment
            </button>
          </div>}
      </Modal>

      {/* Maintenance Request Modal */}
      <Modal isOpen={isMaintenanceOpen} onClose={() => setIsMaintenanceOpen(false)} title="Submit Maintenance Request" data-id="element-718">
        <form onSubmit={handleSubmitRequest} className="space-y-4" data-id="element-719">
          <div data-id="element-720">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-721">
              Issue Type
            </label>
            <select value={issueType} onChange={e => setIssueType(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-722">
              <option data-id="element-723">Plumbing</option>
              <option data-id="element-724">Electrical</option>
              <option data-id="element-725">Appliance</option>
              <option data-id="element-726">Structural</option>
              <option data-id="element-727">Pest Control</option>
              <option data-id="element-728">Other</option>
            </select>
          </div>

          <div data-id="element-729">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-730">
              Urgency
            </label>
            <select value={urgency} onChange={e => setUrgency(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-731">
              <option data-id="element-732">Low</option>
              <option data-id="element-733">Medium</option>
              <option data-id="element-734">High</option>
              <option data-id="element-735">Emergency</option>
            </select>
          </div>

          <div data-id="element-736">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-737">
              Description
            </label>
            <textarea required rows={4} value={description} onChange={e => setDescription(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none resize-none" placeholder="Describe the issue in detail..." data-id="element-738" />
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors mt-4" data-id="element-739">
            Submit Request
          </button>
        </form>
      </Modal>
    </div>;
};